import { useEffect, useCallback } from 'react'
import { AppStore, useIntervalAsync } from 'stylewhere/shared'

export interface VersionCheckConfig {
  frontendVersionStorageKey?: string
  checkInterval?: number | null
}

export const VERSION_CHECK_EVENTS = {
  VERSION_CHECK_START: 'version-check-start',
  VERSION_CHECK_COMPLETE: 'version-check-complete',
  VERSION_UPDATE_DETECTED: 'version-update-detected'
} as const

export const useVersionCheck = ({
  frontendVersionStorageKey = 'rfid_station_frontend_version',
  checkInterval = 5 * 60 * 1000
}: VersionCheckConfig = {}) => {
  const isAuthenticated = !!AppStore.loggedUser

  const checkVersion = useCallback(async () => {
    const checkFrontendVersion = async () => {
      try {
        const response = await fetch('/version.json', {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0'
          }
        })

        if (!response.ok) {
          console.error('Errore nel recupero della versione frontend')
          return false
        }

        const versionInfo = await response.json()
        const currentVersion = versionInfo.version

        if (!currentVersion) return false

        const savedVersion = localStorage.getItem(frontendVersionStorageKey)

        if (savedVersion && savedVersion !== currentVersion) {
          localStorage.setItem(frontendVersionStorageKey, currentVersion)
          return true
        } else if (!savedVersion) {
          localStorage.setItem(frontendVersionStorageKey, currentVersion)
        }

        return false
      } catch (error) {
        console.error('Errore nel controllo versione frontend:', error)
        return false
      }
    }

    try {
      if (!isAuthenticated) {
        return { hasUpdate: false }
      }

      window.dispatchEvent(new CustomEvent(VERSION_CHECK_EVENTS.VERSION_CHECK_START))

      const hasUpdate = await checkFrontendVersion()

      const result = { hasUpdate }

      window.dispatchEvent(
        new CustomEvent(VERSION_CHECK_EVENTS.VERSION_CHECK_COMPLETE, {
          detail: result
        })
      )

      if (result.hasUpdate) {
        window.dispatchEvent(
          new CustomEvent(VERSION_CHECK_EVENTS.VERSION_UPDATE_DETECTED, {
            detail: result
          })
        )
      }

      return result
    } catch (error) {
      console.error('Errore nel controllo versione:', error)
      return { hasUpdate: false }
    }
  }, [isAuthenticated, frontendVersionStorageKey])

  useEffect(() => {
    if (isAuthenticated) {
      checkVersion()
    }
  }, [isAuthenticated, checkVersion])

  useIntervalAsync(async () => {
    if (isAuthenticated) {
      await checkVersion()
    }
  }, checkInterval)

  return { checkVersion }
}
